import { useCaseQuery } from '@/data/Cases/useCaseQuery'
import { useCaseOutputs } from '@/modules/Cases/useCaseOutputs'
import { useCategorisedCaseFiles } from '@/modules/Cases/useCategorisedCaseFiles'
import { useUser } from '@/modules/IdentityAndAccess/useUser'
import { useWorkspaceMembers } from '@/modules/Workspaces/useWorkspaceMembers'
import { computed, type Ref } from 'vue'
import {
  type Bubble,
  type BubbleMessageAgentRun,
  type BubbleMessagePlaceholder,
  type BubbleMessageQuery,
  type BubbleMessageResponse,
  type BubbleMessageToolRun,
  type BubbleSent,
  type Case,
  type CaseInputAttachment,
  type MessageAgentRun,
  type MessageFullResponse,
  type MessagePartialResponse,
  type MessageQuery,
  type MessageToolRun,
} from './types'
import { useCasePendingQueryStore } from './useCasePendingQueryStore'
import { useCaseUploadStore } from './useCaseUploadStore'
import { getMessageAttachments } from './utils'

const getBubbleType = (message: Case['messages'][number]): Bubble['type'] =>
  message.type === 'query' ? 'sent' : 'received'

export const useChatBubbles = ({
  caseId,
  messageIds,
  workspaceId,
}: {
  messageIds: Ref<string[]>
  caseId: Ref<string | undefined | null>
  workspaceId: Ref<string>
}) => {
  const caseQuery = useCaseQuery(caseId, workspaceId)
  const pendingStore = useCasePendingQueryStore()
  const uploadStore = useCaseUploadStore()
  const userStore = useUser()
  const { getMember } = useWorkspaceMembers()

  const getCurrentUser = () =>
    userStore.user
      ? {
          id: userStore.user.id,
          fullName: `${userStore.user.firstName} ${userStore.user.lastName}`,
          email: userStore.user.email || '',
          url: userStore.user.profilePictureUrl,
        }
      : null

  const getUser = (userId: string) => {
    const member = getMember(userId)
    return member
      ? { id: member.id, fullName: member.fullName || null, email: member.email || null }
      : null
  }

  const categorisedCaseFiles = useCategorisedCaseFiles(caseQuery.data)
  const createBubbleMessageQuery = (message: MessageQuery): BubbleMessageQuery => ({
    id: message.id,
    messageType: 'query',
    attachments: getMessageAttachments(
      message,
      categorisedCaseFiles.value.attached[message.id] ?? [],
    ),
    text: message.text,
    user: getUser(message.userId),
    createdAt: message.createdAt,
    updatedAt: message.updatedAt,
    agentId: message.agentId,
  })

  const caseOutputs = useCaseOutputs({ caseId, workspaceId })

  const createBubbleMessageAgentRun = (message: MessageAgentRun): BubbleMessageAgentRun => {
    // a failed or incomplete agent run might lack an outputId, but still has an agent id
    // we look first this specific output, but then fall back to finding any output
    // with the same agent id, in order to at least extract the agent info
    const output = message.outputId
      ? caseOutputs.value.find((o) => o.id === message.outputId)
      : null

    const agent = output?.project || null

    // if outputId was null, outputs is [] regardless of getting agent info
    const outputs =
      message.outputId && output
        ? [{ messageId: message.id, outputId: message.outputId, entityId: output?.entityId }]
        : []

    return {
      id: message.id,
      allMessageIds: [message.id],
      messageType: 'agent_run',
      outputs,
      attachments: [],
      agent: agent
        ? {
            id: agent.id,
            name: agent.name,
            coverHighRes: agent.coverImageUrls.high,
            coverLowRes: agent.coverImageUrls.low,
          }
        : null,
      createdAt: message.createdAt,
      updatedAt: message.updatedAt,
      inProgress: message.inProgress,
    }
  }

  const addToBubbleMessageAgentRun = (bubble: BubbleMessageAgentRun, message: MessageAgentRun) => {
    bubble.allMessageIds.push(message.id)

    const output = message.outputId
      ? caseOutputs.value.find((o) => o.id === message.outputId)
      : null

    if (output) {
      bubble.outputs.push({
        messageId: message.id,
        outputId: output.id,
        entityId: output.entityId,
      })
    }
  }

  const createBubbleMessageResponse = (
    message: MessageFullResponse | MessagePartialResponse,
  ): BubbleMessageResponse | BubbleMessagePlaceholder =>
    // It's possible to have full response with no text.
    // We don't want to be showing "Thinking" in that case as it will never disappear.
    // We don't quite know yet what we do want to show when that happens
    message.text.length == 0 && message.type === 'partial_response'
      ? {
          id: message.id,
          messageType: 'response-placeholder',
          text: 'Thinking',
          createdAt: message.createdAt,
          updatedAt: message.updatedAt,
        }
      : {
          id: message.id,
          messageType: message.type,
          attachments: [],
          sources: message.sources,
          text: message.text,
          createdAt: message.createdAt,
          updatedAt: message.updatedAt,
          suggestions: message.suggestions,
        }

  const createBubbleMessageToolRun = (message: MessageToolRun): BubbleMessageToolRun => ({
    id: message.id,
    messageType: 'tool_run',
    attachments: [],
    toolName: message.toolName,
    createdAt: message.createdAt,
    updatedAt: message.updatedAt,
    inProgress: message.inProgress,
  })

  const createBubble = (message: Case['messages'][number], previousBubble?: Bubble): Bubble => {
    if (message.type === 'query') {
      return { id: message.id, type: 'sent', message: createBubbleMessageQuery(message) }
    }

    const previousQueryId = previousBubble?.type === 'sent' ? previousBubble.message.id : null

    if (message.type === 'agent_run') {
      return {
        id: message.id,
        type: 'received',
        messages: [createBubbleMessageAgentRun(message)],
        previousQueryId,
      }
    }

    if (message.type === 'response' || message.type === 'partial_response') {
      return {
        id: message.id,
        type: 'received',
        messages: [createBubbleMessageResponse(message)],
        previousQueryId,
      }
    }

    return {
      id: message.id,
      type: 'received',
      messages: [createBubbleMessageToolRun(message)],
      previousQueryId,
    }
  }

  const pendingBubbles = computed<BubbleSent[]>(() =>
    pendingStore.pendingQueries
      .filter((q) => q.caseId === caseId.value)
      .map((q) => ({
        id: q.id,
        type: 'sent',
        message: {
          id: q.id,
          messageType: 'pending_query',
          attachments: q.uploadIds
            .map((id) => uploadStore.attachmentsMap.get(id))
            .filter((a): a is CaseInputAttachment => a !== undefined),
          text: q.text,
          user: getCurrentUser(),
          agentId: q.agentId || null,
          createdAt: q.createdAt,
          updatedAt: q.updatedAt,
        },
      })),
  )

  const bubbles = computed(() => {
    const result: Bubble[] = []

    if (!caseQuery.data.value) {
      return result
    }

    const messages = caseQuery.data.value.messages

    messages
      .filter((m) => messageIds.value.includes(m.id))
      .sort((a, b) => (a.createdAt < b.createdAt ? -1 : 1))
      .forEach((message) => {
        const currentBubble = result.at(-1)
        if (!currentBubble || currentBubble.type !== getBubbleType(message)) {
          result.push(createBubble(message, currentBubble))
          return
        }

        if (currentBubble.type === 'received' && message.type === 'agent_run') {
          const lastMessage = currentBubble.messages.at(-1)
          if (
            lastMessage?.messageType === 'agent_run' &&
            lastMessage.agent?.id === message.agentId
          ) {
            addToBubbleMessageAgentRun(lastMessage, message)
          } else {
            currentBubble.messages.push(createBubbleMessageAgentRun(message))
          }
          return
        }

        if (
          currentBubble.type === 'received' &&
          (message.type === 'response' || message.type === 'partial_response')
        ) {
          currentBubble.messages.push(createBubbleMessageResponse(message))
          return
        }

        if (currentBubble.type === 'received' && message.type === 'tool_run') {
          currentBubble.messages.push(createBubbleMessageToolRun(message))
          return
        }
      })

    result.push(...pendingBubbles.value)

    const attachments = pendingBubbles.value.at(0)?.message.attachments || []
    const areSomeUploading = attachments.some((a) => !['processing', 'complete'].includes(a.status))
    if (attachments.length > 0) {
      const now = new Date().toISOString()
      result.push({
        type: 'received',
        id: 'tmp-understanding-files-response',
        messages: [
          {
            id: 'tmp-understanding-files-response',
            messageType: 'response-placeholder',
            text: areSomeUploading ? 'Uploading' : 'Understanding files',
            createdAt: now,
            updatedAt: now,
          },
        ],
        previousQueryId: pendingBubbles.value.at(-1)?.message.id || null,
      })
    }

    return result
  })

  return { bubbles }
}
